import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerTierToken } from '@sleed/bizcommerce-api';
import { customerTiersApi } from '~/lib/api/client';
import { formToToken, type CustomerTierFormValues } from '~/lib/schemas/customer-tier';

export const customerTierKeys = {
	all: ['customer-tiers'] as const,
	list: () => [...customerTierKeys.all, 'list'] as const
};

/** All customer tiers (`GetAll` needs the System role — pass `enabled: false` to skip it when denied). */
export function useCustomerTiersList({ enabled = true }: { enabled?: boolean } = {}) {
	return useQuery<CustomerTierToken[]>({
		queryKey: customerTierKeys.list(),
		queryFn: async () => (await customerTiersApi.getAll()).data.items ?? [],
		enabled
	});
}

export function useCustomerTierMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: customerTierKeys.list() });
	const create = useMutation({
		mutationFn: async (form: CustomerTierFormValues) => (await customerTiersApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: CustomerTierFormValues) => (await customerTiersApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customerTiersApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
